import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import "./SellerApplicationsManager.css";

const fmt=value=>value?new Date(value).toLocaleString():"—";

function StatusBadge({row,current}) {
  let label=row.application_status||"Unknown";
  if(current && row.application_status==="pending" && Number(row.submission_number)>1) label="Resubmitted";
  return <span className={`seller-app-status ${(row.application_status||"").toLowerCase()} ${label==="Resubmitted"?"resubmitted":""}`}>{label}</span>;
}

function ApplicationDetails({row,historical=false}) {
  return <>
    <div className="seller-app-titleline">
      <div><strong>{row.shop_name}</strong> <small>/{row.shop_slug}</small></div>
      <StatusBadge row={row} current={!historical}/>
    </div>
    <p>{row.shop_description||"No shop description supplied."}</p>
    <div className="seller-app-categories">{(row.item_categories||[]).map(c=><span key={c}>{c}</span>)}</div>
    <div className="seller-app-meta">
      <span>Applicant: {row.applicant_display}</span>
      <span>Submitted: {fmt(row.submitted_at)}</span>
      <span>Submission #{row.submission_number||1}</span>
      <span>Seller Agreement: {row.seller_agreement_version||row.terms_version||"—"}</span>
      <span>Rules: {row.marketplace_rules_version||"—"}</span>
    </div>
    {row.review_note&&<div className="seller-app-review-note"><strong>Review note</strong><span>{row.review_note}</span></div>}
  </>;
}

export default function SellerApplicationsManager(){
  const [rows,setRows]=useState([]),[loading,setLoading]=useState(true),[error,setError]=useState("");
  const [notes,setNotes]=useState({}),[busy,setBusy]=useState(null),[openHistory,setOpenHistory]=useState({});

  const load=useCallback(async()=>{
    setLoading(true);setError("");
    const {data,error:e}=await supabase.rpc("admin_list_seller_applications");
    if(e)setError(e.message||"Unable to load seller applications.");
    else setRows(data||[]);
    setLoading(false);
  },[]);
  useEffect(()=>{load()},[load]);

  const threads=useMemo(()=>{
    const map=new Map();
    rows.forEach(r=>{
      const key=r.application_thread_id||r.application_id;
      if(!map.has(key))map.set(key,[]);
      map.get(key).push(r);
    });
    return [...map.entries()].map(([key,items])=>{
      items.sort((a,b)=>(Number(b.submission_number||1)-Number(a.submission_number||1)));
      return {key,current:items.find(x=>x.is_latest_submission) || items[0],history:items.filter(x=>!(x.is_latest_submission)).sort((a,b)=>Number(b.submission_number||1)-Number(a.submission_number||1))};
    }).sort((a,b)=>new Date(b.current.submitted_at)-new Date(a.current.submitted_at));
  },[rows]);

  const review=async(row,decision)=>{
    if(row.application_status!=="pending" || !row.is_latest_submission)return;
    setBusy(row.application_id);setError("");
    const {error:e}=await supabase.rpc("admin_review_seller_application",{
      p_application_id:row.application_id,
      p_decision:decision,
      p_review_note:(notes[row.application_id]||"").trim()||null
    });
    if(e)setError(e.message||`Unable to ${decision} application.`);
    else await load();
    setBusy(null);
  };

  return <section className="seller-app-admin">
    <div className="seller-app-head"><div><h2>Seller applications</h2><p>Approve marketplace sellers before Seller Dashboard access is enabled. Historical submissions remain available as an audit trail.</p></div><button onClick={load} disabled={loading}>Refresh</button></div>
    {error&&<div className="seller-app-error">{error}</div>}
    {loading?<div className="seller-app-empty">Loading seller applications…</div>:threads.length===0?<div className="seller-app-empty">No seller applications yet.</div>:
      <div className="seller-app-list">{threads.map(({key,current,history})=><article className="seller-app-thread" key={key}>
        <div className="seller-app-row seller-app-current">
          <div className="seller-app-copy"><ApplicationDetails row={current}/></div>
          <div className="seller-app-review">
            {current.application_status==="pending" && current.is_latest_submission ? <>
              <textarea value={notes[current.application_id]||""} onChange={e=>setNotes(n=>({...n,[current.application_id]:e.target.value}))} placeholder="Review note (optional)"/>
              <div className="seller-app-actions">
                <button className="approve" disabled={busy===current.application_id} onClick={()=>review(current,"approved")}>Approve</button>
                <button className="reject" disabled={busy===current.application_id} onClick={()=>review(current,"rejected")}>Reject</button>
              </div>
            </>:<div className="seller-app-closed">No action required</div>}
          </div>
        </div>

        {history.length>0&&<div className="seller-app-history">
          <button className="seller-app-history-toggle" onClick={()=>setOpenHistory(s=>({...s,[key]:!s[key]}))}>
            {openHistory[key]?"Hide":"Application"} History ({history.length}) <span>{openHistory[key]?"▴":"▾"}</span>
          </button>
          {openHistory[key]&&<div className="seller-app-history-list">{history.map(h=><div className="seller-app-history-row" key={h.application_id}>
            <ApplicationDetails row={h} historical/>
            <div className="seller-app-history-closed">Historical submission — no admin actions</div>
          </div>)}</div>}
        </div>}
      </article>)}</div>}
  </section>;
}